// FormTrack tracking script. Single IIFE, vanilla, ES2019. Never throws.
// Loaded as <script async src=".../ft.js" data-site="..."></script>.
import { uuidv4 } from './lib/uuid.js';
import { readUTMs } from './lib/utms.js';
import { labelFor, extractFields, isPasswordField } from './lib/labels.js';
import { createDedup } from './lib/dedup.js';
import { injectHoneypot, injectHoneypotsInto } from './lib/honeypot.js';
import { looksLikeTracker, hasRealContactField } from './lib/noise-filter.js';
import { createRegistry, formIdentifierFor } from './lib/registry.js';
import { readFlags, flagEnabled } from './lib/flags.js';
import { sanitizeStack } from './lib/sanitize-stack.js';
import { sanitizeUrl } from './lib/sanitize-url.js';

(function () {
  'use strict';
  try {
    var SCRIPT_VERSION = '1.0.1-wixfix';
    var SCRIPT = document.currentScript;
    if (!SCRIPT) {
      // Fallback: pick the last script tag with data-site.
      var all = document.getElementsByTagName('script');
      for (var i = all.length - 1; i >= 0; i--) {
        if (all[i].getAttribute && all[i].getAttribute('data-site')) { SCRIPT = all[i]; break; }
      }
    }
    var SITE_KEY = SCRIPT && SCRIPT.getAttribute('data-site');
    if (!SITE_KEY) { try { console.warn('[ft] missing data-site'); } catch (e) {} return; }

    // Canonical FormTrack origin baked at build time (script/build.mjs).
    // Used as the trust anchor when SCRIPT.src has been rewritten by a
    // caching/minify plugin to the customer's own domain.
    //
    // Computed BEFORE the init guard below (QA16 W-FTJS) — the guard's key is
    // `${API_BASE}::${SITE_KEY}`, so API_BASE must exist first.
    var FT_CANONICAL = (process.env.FT_CANONICAL_API_BASE || '');
    var API_BASE = (function () {
      // 1. Explicit override on the snippet wins. Cache plugins generally
      //    rewrite the src URL but leave data-* attributes alone, so the
      //    install snippet now ships data-api by default.
      var explicit = SCRIPT && SCRIPT.getAttribute('data-api');
      if (explicit) return explicit.replace(/\/+$/, '');
      // 2. Derive from SCRIPT.src — only if the host looks like a
      //    FormTrack origin (vercel.app preview/prod, localhost dev,
      //    or the canonical host). Anything else means a cache plugin
      //    rehosted ft.js on the customer domain; trust the canonical
      //    fallback instead of producing a same-origin 404 trap.
      var derived = SCRIPT && SCRIPT.src ? SCRIPT.src.replace(/\/ft\.js.*$/, '') : '';
      if (derived) {
        var host = '';
        try { host = new URL(derived).hostname; } catch (e) { host = ''; }
        var canonHost = '';
        try { host && FT_CANONICAL && (canonHost = new URL(FT_CANONICAL).hostname); } catch (e) {}
        var trusted = host && (
          host === canonHost ||
          /\.vercel\.app$/i.test(host) ||
          host === 'localhost' ||
          /^127\./.test(host) ||
          /^192\.168\./.test(host) ||
          /^10\./.test(host)
        );
        if (trusted) return derived;
      }
      // 3. Fall back to the build-time canonical origin.
      return FT_CANONICAL;
    })();

    // One-time init guard, keyed by (data-api, data-site) — QA16 W-FTJS.
    // Multiple distinct trackers on ONE page is a permanently supported
    // capability (Steven, locked): a staging embed and a prod embed (or any
    // two embeds with different data-api OR different data-site) must BOTH
    // run. Only an EXACT duplicate — same api AND same site — is blocked,
    // since that pairing would otherwise bind a SECOND delegated 'submit'
    // listener on document for the identical backend, so a single form
    // submit POSTs twice (each with its own submission_id), creating two
    // leads / two notifications for that one backend. Server-side
    // content-dedup (step 4b in /api/capture) is the backstop for embeds we
    // can't control.
    //
    // (Was a bare `window.__ft_loaded` boolean — blocked ANY second embed
    // unconditionally, with no api/site awareness. Two other global-scope
    // constructs (the fetch/XHR AJAX-detection wrap, registry.js's
    // localStorage cache key) also needed per-instance isolation alongside
    // this guard — see below and lib/registry.js.)
    var INSTANCE_KEY = API_BASE + '::' + SITE_KEY;
    window.__ft_instances = window.__ft_instances || {};
    if (window.__ft_instances[INSTANCE_KEY]) {
      try { console.warn('[ft] already initialised for this api+site — ignoring duplicate embed'); } catch (e) {}
      return;
    }
    window.__ft_instances[INSTANCE_KEY] = true;

    function safe(fn) { try { return fn(); } catch (e) { /* never throw */ return undefined; } }

    // N-instance hardening: namespaced by INSTANCE_KEY (api+site), not a bare
    // 'ft_session' literal — an unnamespaced key would have every instance on
    // the page (however many) share ONE session_id (last-writer-wins), which
    // is harmless to any ONE backend's own data (each backend is internally
    // self-consistent regardless) but is not genuinely per-tracker isolation.
    // No other code reads this key by its old fixed name (checked), so
    // namespacing it is a pure correctness improvement, not a behavior change
    // any existing consumer depends on.
    var SESSION_KEY = 'ft_session_' + INSTANCE_KEY;
    var SESSION_ID = safe(function () { return sessionStorage.getItem(SESSION_KEY); }) || uuidv4();
    safe(function () { sessionStorage.setItem(SESSION_KEY, SESSION_ID); });

    var VISITOR_KEY = null;
    var dedup = createDedup();
    var registry = null; // set after boot — see initCookie callback
    var lastSeenFields = {}; // buffered field values for thank-you observer

    // ── Wix submission contract (hotfix port of the staging rule) ────────────
    // A Wix form-app form (id `form-<uuid>`) becomes a Lead ONLY from Wix's own answer to its CreateSubmission call
    // (an id and status CONFIRMED or PENDING). A click, Enter, a native submit, a form-looking POST or thank-you text
    // prove nothing: a CAPTCHA-gated Submit, a multi-step "Next" and "Upload File" all look like "Submit" to a click
    // handler, which stored one enquiry as several Leads or a Lead Wix never accepted. Every other form is unchanged.
    // That call is issued from a Wix Web Worker, invisible to page fetch/XHR on the current editor, so the OUTCOME is
    // read from the workflow result Wix pushes to the form over a MessageChannel (Method 2c). Fails closed.
    var WIX_FORM_APP_ID = /^form-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    var WIX_SUBMIT_URL = /\/form-submission-service\/v\d+\/submissions(?:[?#]|$)/;
    var WIX_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    var WIX_GESTURE_MS = 120000;
    var wixConfirmed = {};          // Wix submission ids already sent by this page visit
    var lastGestureFormId = '';     // the form the visitor last clicked inside, and when
    var lastGestureFormAt = 0;
    var lastSeenFormId = '';        // the non-Wix form the buffered thank-you fields came from
    function formIdOf(form) {
      return (form && form.getAttribute && (form.getAttribute('action') || form.getAttribute('id') || form.getAttribute('name'))) || '';
    }
    // Wix's submission id as a Lead idempotency key. /api/capture validates `submission_id` as a strict RFC UUID
    // (version 1-8, variant 8-b), which an arbitrary Wix GUID need not satisfy, so the version and variant digits are
    // forced. Deterministic: the same Wix submission id always yields the same key (replay = one Lead).
    function wixSubmissionUuid(id) {
      var h = String(id).toLowerCase().replace(/-/g, '');
      h = h.slice(0, 12) + '4' + h.slice(13, 16) + ((parseInt(h.charAt(16), 16) & 3) | 8).toString(16) + h.slice(17);
      return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
    }
    // The form a capture with no form id of its own (an AJAX POST's URL, a thank-you's page path) belongs to.
    function originForm() {
      if (lastGestureFormId && Date.now() - lastGestureFormAt <= WIX_GESTURE_MS) return lastGestureFormId;
      return lastSeenFormId;
    }

    // ── Feature flags / kill switch (2e) ────────────────────────────────────
    // Per-capability flags fetched at boot from /api/cookie (d.flags). Server-
    // controlled (app_config.ft_flags), so a misbehaving capability can be
    // flipped OFF at runtime without rolling back the script or the correctness
    // fixes. DEFAULT-SAFE: an absent/blocked/failed config leaves FLAGS = {} so
    // flagEnabled(FLAGS, name) is false — every gated capability stays off until
    // the server explicitly turns it on. Capture is NEVER gated by a flag
    // (INV-9/10/11): the lead path runs regardless of flag state.
    // First consumer (Phase 8): `if (flagEnabled(FLAGS, 'dwell')) { … }`.
    var FLAGS = {};

    // ── Dwell / engaged time (8a) ───────────────────────────────────────────
    // Behind the kill switch (flagEnabled(FLAGS,'dwell'), default OFF). A
    // visibility-gated active-time accumulator + a SINGLE pagehide exit beacon
    // (bfcache/iOS-safe — never unload/beforeunload). Per-pageview client_pv_id
    // lets /api/dwell match the reading to its page_visits row. EVERY function
    // here is wrapped by callers in safe(); a dwell exception can never reach
    // capture (INV-2). Capture/pageview do not depend on any of this (INV-1).
    var CLIENT_PV_ID = null;   // per-pageview id, minted in pageview()
    var dwellActiveMs = 0;     // accumulated VISIBLE time for the current pageview
    var dwellResumeAt = 0;     // ts when the page last became visible (0 = paused)
    var dwellSent = false;     // at most one exit beacon per pageview
    var DWELL_CAP_S = 1800;    // 30-min cap

    function dwellResume() { if (dwellResumeAt === 0) dwellResumeAt = Date.now(); }
    function dwellPause() {
      if (dwellResumeAt !== 0) { dwellActiveMs += Date.now() - dwellResumeAt; dwellResumeAt = 0; }
    }
    function dwellSeconds() {
      var ms = dwellActiveMs + (dwellResumeAt !== 0 ? Date.now() - dwellResumeAt : 0);
      var s = Math.round(ms / 1000);
      return s > DWELL_CAP_S ? DWELL_CAP_S : s;
    }
    // Flush the current pageview's engaged time, then reset for a new pageview.
    function dwellFlush() {
      if (dwellSent || !CLIENT_PV_ID || !flagEnabled(FLAGS, 'dwell')) return;
      dwellPause();
      var s = dwellSeconds();
      if (s <= 0) return;
      dwellSent = true;
      send('/api/dwell', {
        site_key: SITE_KEY,
        client_pv_id: CLIENT_PV_ID,
        dwell_s: s,
        timestamp: new Date().toISOString()
      });
    }
    function dwellResetFor(pvId) {
      CLIENT_PV_ID = pvId;
      dwellActiveMs = 0;
      dwellResumeAt = 0;
      dwellSent = false;
      if (flagEnabled(FLAGS, 'dwell') && !document.hidden) dwellResume();
    }
    // Wire the visibility + exit listeners once (after flags are known). No-ops
    // when the flag is off (dwellFlush guards on it), so wiring is harmless.
    var dwellWired = false;
    function setupDwell() {
      if (dwellWired) return;
      dwellWired = true;
      safe(function () {
        document.addEventListener('visibilitychange', function () {
          safe(function () { if (document.hidden) dwellPause(); else dwellResume(); });
        });
        // pagehide covers tab-close, navigation, and mobile-background/bfcache —
        // unlike unload it fires reliably on iOS and is bfcache-safe.
        window.addEventListener('pagehide', function () { safe(dwellFlush); });
      });
    }

    // ── Pageview dedupe ─────────────────────────────────────────────────
    // The initial load fires pageview(), and host frameworks (Next.js
    // hydration, some WordPress themes) call history.replaceState() a few
    // ms later — which the history wrapper also routes through pageview().
    // That produces two page_visits rows for the same (session, path) ms
    // apart, inflating views/pages-per-visit and masking genuine 1-page
    // bounces (the rollup counts rows). Collapse same-path beacons fired
    // within 1s into one; genuine SPA nav (different path) and later
    // same-path re-visits (>1s) still send.
    var LAST_PV_KEY = null;
    var LAST_PV_AT = 0;

    // ── Submission token (M3 abuse protection) ──────────────────────────
    // Lazily fetched after the first /api/pageview. The token is signed
    // by the server (60s TTL, bound to script_key + visitor_key + IP) and
    // attached as `data.token` on every /api/capture. The server never
    // rejects on a missing/expired token — it just flags `token_missing`
    // so the lead can be scored. We refresh proactively when within ~10s
    // of the token's expiry so a quick form submission isn't held up.
    var TOKEN_VALUE = null;
    var TOKEN_EXPIRES_AT = 0;
    var tokenFetchInFlight = false;

    function refreshTokenIfStale() {
      if (!VISITOR_KEY) return;
      if (tokenFetchInFlight) return;
      var now = Date.now();
      if (TOKEN_VALUE && (TOKEN_EXPIRES_AT - now) > 10000) return;
      tokenFetchInFlight = true;
      try {
        fetch(API_BASE + '/api/token', {
          method: 'POST',
          credentials: 'include',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            site_key: SITE_KEY,
            visitor_key: VISITOR_KEY
          })
        }).then(function (r) {
          if (!r.ok) return null;
          return r.json();
        }).then(function (d) {
          tokenFetchInFlight = false;
          if (!d || !d.token) return;
          TOKEN_VALUE = d.token;
          TOKEN_EXPIRES_AT = Date.now() + (Math.max(10, (d.expires_in || 60) - 5) * 1000);
        })['catch'](function () { tokenFetchInFlight = false; });
      } catch (e) { tokenFetchInFlight = false; }
    }
    // Many form libraries (Elementor, WPForms, etc.) fire BOTH native submit
    // and a follow-up AJAX request, with submit running first. Track when a
    // native submit fired so the fetch/XHR intercept can suppress its own
    // capture during the AJAX echo. Different libs use different ajax URLs
    // (e.g. wp-admin/admin-ajax.php) so per-form dedup misses; this tracks
    // by time-window instead.
    var lastNativeSubmitAt = 0;

    function warn(msg) { try { if (window.console && window.console.warn) window.console.warn('[ft] ' + msg); } catch (e) {} }

    function send(path, payload) {
      var url = API_BASE + path;
      var body;
      try { body = JSON.stringify(payload); } catch (e) {
        warn('payload serialise failed for ' + path);
        return;
      }
      // sendBeacon is the primary transport — survives page unload, but
      // returns false on quota / unsupported scheme / browser policy.
      try {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) { /* fall through */ }
      // fetch+keepalive fallback. Surface failures via console.warn so
      // customers debugging "leads aren't arriving" have a visible
      // signal instead of a silent drop.
      try {
        fetch(url, {
          method: 'POST', body: body, keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', mode: 'cors'
        }).then(function (r) {
          if (!r.ok) warn(path + ' returned ' + r.status);
        })['catch'](function (e) {
          warn(path + ' fetch failed: ' + (e && e.message ? e.message : 'network'));
        });
      } catch (e) {
        warn(path + ' transport unavailable');
      }
    }

    function initCookie(cb) {
      try {
        fetch(API_BASE + '/api/cookie', { credentials: 'include', mode: 'cors' })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            VISITOR_KEY = (d && d.visitor_key) || uuidv4();
            // 2e: adopt server flags; readFlags is total + default-safe ({} when
            // absent/malformed), so every gated capability stays off by default.
            FLAGS = readFlags(d && d.flags);
            cb();
          })
          ['catch'](function () { VISITOR_KEY = uuidv4(); cb(); });
      } catch (e) { VISITOR_KEY = uuidv4(); cb(); }
    }

    // The install snippet's inline IIFE stashes the first external
    // document.referrer in `sessionStorage.ft_origin_ref` synchronously
    // during HTML parsing — before any WP optimisation plugin can
    // touch the page, before any anchor click can navigate. We read
    // it here as the authoritative referrer; falls back to
    // document.referrer when the stash is missing (older installs
    // that haven't pasted the new snippet yet, or sessionStorage
    // blocked by privacy mode). The stash survives same-origin
    // navigation (Google → home → /contact/) so the multi-page flow
    // captures the real upstream referrer instead of the same-origin
    // homepage URL.
    function authoritativeReferrer() {
      try {
        var s = sessionStorage.getItem('ft_origin_ref');
        if (s) return s;
      } catch (e) { /* sessionStorage blocked — fall back */ }
      return document.referrer || '';
    }

    function pageview() {
      // Collapse the load + hydration-replaceState double-fire: same path
      // within 1s is treated as one pageview. Different path (SPA nav) or
      // a later same-path re-visit (>1s) proceeds normally.
      var key = location.pathname + location.search;
      var now = Date.now();
      if (key === LAST_PV_KEY && now - LAST_PV_AT < 1000) return;
      LAST_PV_KEY = key;
      LAST_PV_AT = now;
      // Dwell (8a): this is a genuine new pageview (deduped above). Flush the
      // PRIOR page's engaged time (SPA nav away), then mint a fresh client_pv_id
      // and reset — so SPA navigation never double-counts. safe(): isolated from
      // the capture/pageview send below (INV-2).
      safe(dwellFlush);
      var clientPvId = uuidv4();
      safe(function () { dwellResetFor(clientPvId); });
      var utms = readUTMs(location.search);
      send('/api/pageview', {
        site_key: SITE_KEY,
        visitor_key: VISITOR_KEY,
        page_url: sanitizeUrl(location.href),
        page_path: location.pathname,
        page_title: document.title || '',
        referrer: sanitizeUrl(authoritativeReferrer()),
        timestamp: new Date().toISOString(),
        session_id: SESSION_ID,
        client_pv_id: clientPvId,
        utm_source: utms.utm_source,
        utm_medium: utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        // Click IDs on the pageview beacon (not just capture). First-touch is
        // seeded from the FIRST pageview, so without these a paid click that
        // lands on the ad page but submits its form elsewhere (e.g. a
        // cross-origin Jotform) loses its gclid/fbclid/msclkid and misclassifies
        // as organic. Same live-query source as capture() (readUTMs); empty
        // string when absent so the payload shape stays stable.
        gclid: utms.gclid,
        fbclid: utms.fbclid,
        msclkid: utms.msclkid
      });
      // Proactively refresh the submission token shortly after the
      // pageview lands server-side. /api/token requires a visitor with
      // a fresh heartbeat, so we wait a beat to give the pageview write
      // time to commit before asking for a token.
      setTimeout(refreshTokenIfStale, 750);
    }

    function findEmail(fields) {
      for (var k in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, k) && /email/i.test(k)) return fields[k];
      }
      return '';
    }

    // Detect, WITHOUT reading any value, whether a form collects a password
    // (and a confirm/repeat password). Used only to send value-free presence
    // signals so the server can classify login/signup forms (PR 3a). We read
    // input TYPE and attribute NAMES only — never .value (see #681).
    // Is this control part of a FIELD's own UI rather than an action on the form?
    //
    // ── THE DEFECT THIS CLOSES ──────────────────────────────────────────────
    //
    // Method 1b treats every `<button type="button">` inside a <form> as a
    // submit control. On a real Wix contact form that set also contains the
    // phone country-code selector — a control the visitor MUST click to fill
    // the form in. Measured on bettergardens.ae 2026-09-21: a click on it with
    // only `Name` typed shipped a one-field lead into the customer's inbox.
    // Raising the populated-field threshold would not have fixed that; it would
    // have moved it, and still fired once the form happened to be complete.
    //
    // ── WHY THESE FOUR SIGNALS, MEASURED NOT GUESSED ────────────────────────
    //
    // scripts/recon/widget-control-attrs-probe.mjs dumped every attribute of
    // every candidate control on two independent Wix sites, four forms:
    //
    //   phone country selector  aria-expanded="false" aria-haspopup="listbox"
    //   project-type dropdown   aria-expanded="false" role="combobox"
    //                           aria-controls="dropdown-options-container_-15"
    //   Submit  x4              none of the above, on either site
    //
    // So the separation is clean, and it is standard ARIA in both directions:
    // a disclosure/popup control announces itself with aria-expanded,
    // aria-haspopup or aria-controls, and a widget announces its own role. A
    // control that submits a form carries none of them. NOTHING here is
    // builder-specific — `data-hook="country-selector-trigger"` would have
    // identified the same element and is deliberately NOT used, because it
    // would fix Wix and leave every other builder broken.
    //
    // ── TWO HEURISTICS CONSIDERED AND REJECTED ON THE MEASUREMENT ───────────
    //
    // "the control shares a wrapper with an input" — REJECTED: the newsletter
    // form's Submit on bettergardens.ae has that property too, so this would
    // have silently disabled capture on that form.
    // "the control is the last button in the form" — REJECTED: true of all four
    // Submits measured, but it is positional luck, not meaning; a trailing
    // reset/back/cookie control breaks it and the failure is silent.
    //
    // Roles a control carries when it IS a field's input surface or part of one.
    // `dialog` and `listitem` were considered and dropped: a button carrying
    // either is malformed, and the real case — a control that OPENS a dialog —
    // is already caught by aria-haspopup.
    var WIDGET_ROLES = {
      combobox: 1, listbox: 1, option: 1, menu: 1, menubar: 1, menuitem: 1,
      menuitemcheckbox: 1, menuitemradio: 1, tab: 1, tablist: 1, switch: 1,
      checkbox: 1, radio: 1, spinbutton: 1, slider: 1, tree: 1, treeitem: 1
    };
    function isFieldWidget(el) {
      try {
        if (!el || !el.getAttribute) return false;
        // Disclosure/popup semantics: it opens or controls something else.
        if (el.hasAttribute('aria-expanded')) return true;
        if (el.hasAttribute('aria-haspopup')) return true;
        if (el.hasAttribute('aria-controls')) return true;
        // An explicit widget role. `role="button"` is NOT in WIDGET_ROLES —
        // that is the generic action role and is exactly what Method 1b wants.
        var role = (el.getAttribute('role') || '').toLowerCase().trim();
        if (role && WIDGET_ROLES[role]) return true;
        // Inside a <label> is a field's own furniture by definition.
        if (el.closest && el.closest('label')) return true;
        return false;
      } catch (e) {
        // Never throw out of a click handler. Treating an unreadable control as
        // NOT a widget preserves the pre-existing capture behaviour rather than
        // silently dropping leads — fail toward the old path, not toward none.
        return false;
      }
    }

    function detectPasswordSignals(form) {
      var result = { hasPassword: false, hasConfirmPassword: false };
      try {
        if (!form || typeof form.querySelectorAll !== 'function') return result;
        var pw = form.querySelectorAll('input[type=password]');
        result.hasPassword = pw.length > 0;
        if (pw.length >= 2) {
          result.hasConfirmPassword = true;
        } else if (pw.length === 1) {
          var el = pw[0];
          var hint = (
            (el.getAttribute('name') || '') + ' ' +
            (el.getAttribute('id') || '') + ' ' +
            (el.getAttribute('placeholder') || '') + ' ' +
            (el.getAttribute('autocomplete') || '')
          ).toLowerCase();
          if (/confirm|repeat|retype|re-?enter|verify|new[\s_-]*password/.test(hint)) {
            result.hasConfirmPassword = true;
          }
        }
      } catch (e) { /* presence detection is best-effort */ }
      return result;
    }

    // Returns TRUE when this call actually dispatched to /api/capture, FALSE
    // when a gate suppressed it. Every caller may ignore the value — nothing
    // behavioural depends on it — but the debug wrapper reads it so the
    // diagnostic stream can stop claiming a suppressed call was dispatched.
    // Added because the operator panel showed "dispatched: yes" for calls that
    // client dedup had silently swallowed, which made a duplicate-capture
    // report impossible to confirm or refute from the stream.
    function capture(formIdentifier, fields, opts) {
      if (!fields || typeof fields !== 'object') return false;
      // Wix submission contract: a Wix form-app form leaves here ONLY with Wix's own confirmation. `origin` names the
      // form for captures whose own identifier is a URL or a page path (AJAX POST, thank-you).
      var wixEvidence = !!(opts && opts.evidence === 'wix_submission_confirmed' && WIX_GUID.test(String(opts.wixSubmissionId || '')));
      if (!wixEvidence && (WIX_FORM_APP_ID.test(String(formIdentifier || '')) || WIX_FORM_APP_ID.test(String((opts && opts.origin) || '')))) return false;
      // Require at least one populated field — we don't ship empty submissions.
      var hasAny = false;
      for (var k in fields) { if (Object.prototype.hasOwnProperty.call(fields, k) && fields[k]) { hasAny = true; break; } }
      if (!hasAny) return false;
      // Customer-excluded form (set on /install): never ship. Saves a
      // round trip and keeps the customer's "this is not a lead form"
      // intent honoured even before the server-side gate runs.
      if (registry && registry.isExcluded(String(formIdentifier || ''))) return false;
      var email = findEmail(fields);
      if (dedup.isDuplicate(email, formIdentifier)) return false;
      var utms = readUTMs(location.search);
      var payload = {
        // A Wix-confirmed capture is identified by Wix's own submission id: the server already makes
        // leads.submission_id idempotent (23505 = success), so a replay or a second tracker is ONE Lead.
        submission_id: wixEvidence ? wixSubmissionUuid(opts.wixSubmissionId) : uuidv4(),
        site_key: SITE_KEY,
        visitor_key: VISITOR_KEY || uuidv4(),
        page_url: sanitizeUrl(location.href),
        page_path: location.pathname,
        referrer: sanitizeUrl(authoritativeReferrer()),
        // 2b: no location.pathname fallback. An empty identifier is sent as-is;
        // the server maps empty/pure-path identifiers to a pending_review
        // "unidentified" form and still captures the lead (INV-5). Keeping the
        // pathname fallback here would re-introduce the path-junk form rows.
        form_identifier: String(formIdentifier || ''),
        fields: fields,
        session_id: SESSION_ID,
        script_version: SCRIPT_VERSION,
        timestamp: new Date().toISOString(),
        utm_source: utms.utm_source,
        utm_medium: utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        utm_term: utms.utm_term,
        utm_content: utms.utm_content,
        gclid: utms.gclid,
        fbclid: utms.fbclid,
        msclkid: utms.msclkid,
        // Value-free risky-form signals (PR 3a). Present only on the native
        // submit path where the form element is available; omitted elsewhere
        // (the server URL-pattern backstop covers those). Never carries values.
        // (`opts` may now carry only `origin` / Wix evidence: absent password signals stay omitted, as before.)
        has_password: opts && opts.hasPassword !== undefined ? opts.hasPassword === true : undefined,
        has_confirm_password: opts && opts.hasConfirmPassword !== undefined ? opts.hasConfirmPassword === true : undefined,
        // Soft signal — server tolerates missing/expired tokens.
        token: TOKEN_VALUE || undefined,
        // When a diagnostic session is active, surface the session token
        // on the capture body so /api/capture can mark the lead is_test
        // and skip stats + notification. Field is `undefined` in the
        // common non-debug path so the JSON serializer omits it —
        // production payloads stay byte-identical.
        debug_session_token: DEBUG_TOKEN || undefined
      };
      send('/api/capture', payload);
      // Submitting consumes the token (server treats it as one-shot for
      // dedup). Drop it locally so a subsequent submission triggers a
      // refresh on the next pageview tick.
      TOKEN_VALUE = null;
      TOKEN_EXPIRES_AT = 0;
      return true;
    }

    // Method 1: native form submit (capture phase, delegated).
    document.addEventListener('submit', function (e) {
      safe(function () {
        var form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        lastNativeSubmitAt = Date.now();
        // 2a: read identity via getAttribute — form.id / form.name DOM properties
        // are shadowed by <input name="id"> / <input name="name">, yielding
        // "[object HTMLInputElement]". 2b: no location.pathname fallback — a form
        // with no real identifier sends an empty id; the server records it as a
        // pending_review (unidentified) form and still captures the lead (INV-5).
        var id = form.getAttribute('action') || form.getAttribute('id') || form.getAttribute('name') || '';
        var fields = extractFields(form);
        lastSeenFields = fields;
        capture(id, fields, detectPasswordSignals(form));
      });
    }, true);

    // Method 1b: a submit control that fires NO native submit event.
    //
    // ── WHY METHOD 1 IS NOT ENOUGH ──────────────────────────────────────────
    //
    // Method 1 hangs off the native `submit` event. A `<button type="button">`
    // does not raise one — the framework's own click handler posts the data and
    // navigates. Measured on bettergardens.ae (Wix Forms, form-app 1.3080.0):
    // every form's submit control is `<button type="button">` inside a
    // `<fieldset>`, so Method 1 never fires. That site logged script_loaded and
    // eight form_detected events and zero captures.
    //
    // The other three methods do not cover it either, which is why this is a new
    // path rather than a tweak to an existing one:
    //   Method 2 (fetch/XHR) — Wix POSTs a NESTED JSON envelope, and
    //     inspectBody's flattener only copies TOP-LEVEL string/number values,
    //     so `fields` comes out near-empty. Even flattened, Wix's keys are field
    //     UUIDs, and hasRealContactField() matches on the KEY (email|phone|
    //     name|…), so it returns false and the body is dropped.
    //   Method 3 (thank-you observer) — Wix redirects on success, so the
    //     thank-you text never appears in this document. This is the whole
    //     defect: a Wix form set to show an INLINE message is already rescued by
    //     Method 3, a Wix form set to REDIRECT captures nothing at all.
    //   Method 4 (postMessage) — for cross-origin iframes; this form is
    //     same-origin DOM.
    //
    // ── WHY THIS IS SAFE TO FIRE ON A CLICK ─────────────────────────────────
    //
    // A click is a much weaker signal than a submit, so the gates are
    // deliberately tight and all of them must hold:
    //   - the control is a button-ish element whose type is NOT submit (a
    //     `type="submit"` button already raises the native event — Squarespace
    //     ships exactly that, and double-firing it is the thing to avoid);
    //   - it is NOT a field widget — see isFieldWidget above;
    //   - it is inside a real <form> we can read;
    //   - a native submit has not fired in the last 2s (same window Method 2
    //     uses) — belt and braces against a framework that does both;
    //   - extractFields finds at least one POPULATED data-bearing field.
    // capture() then applies its own email+identifier dedup, so a double-click
    // or a framework that also triggers an AJAX post cannot produce two leads.
    //
    // Field VALUES come from extractFields, which keys on labelFor: aria-label
    // first. That is what makes Wix work — its inputs carry
    // aria-label="Email"/"Name"/"Phone" and NO name attribute, so a
    // name-keyed reader would come back empty while this one comes back with
    // real, human-readable keys.
    document.addEventListener('click', function (e) {
      safe(function () {
        var el = e.target;
        if (!el || !el.closest) return;
        var gform = el.closest('form');
        if (gform) { lastGestureFormId = formIdOf(gform); lastGestureFormAt = Date.now(); }
        var btn = el.closest('button, input[type=button], [role=button]');
        if (!btn) return;
        var btype = (btn.getAttribute && (btn.getAttribute('type') || '')).toLowerCase();
        // A real submit control is Method 1's job. `<button>` with no type
        // defaults to submit, so an ABSENT type is also Method 1's.
        if (btn.tagName === 'BUTTON' && btype !== 'button') return;
        if (btn.tagName === 'INPUT' && btype !== 'button') return;
        // A click on a field's own UI is not a submission.
        if (isFieldWidget(btn)) return;
        var form = btn.closest('form');
        if (!form) return;
        if (Date.now() - lastNativeSubmitAt < 2000) return;
        // A click on a Wix form-app form is never a submission (contract above): no capture. Wix's own confirmation
        // (Method 2b/2c) is the only evidence; the thank-you observer is stopped by the gesture's origin form.
        if (WIX_FORM_APP_ID.test(formIdOf(form))) return;
        var fields = extractFields(form);
        var hasAny = false;
        for (var k in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, k) && fields[k]) { hasAny = true; break; }
        }
        if (!hasAny) return;
        var id = form.getAttribute('action') || form.getAttribute('id') || form.getAttribute('name') || '';
        lastSeenFields = fields;
        lastSeenFormId = id;
        capture(id, fields, detectPasswordSignals(form));
      });
    }, true);

    // Buffer field values as the user types — feeds the thank-you observer.
    document.addEventListener('change', function (e) {
      safe(function () {
        var t = e.target;
        if (!t || !t.form) return;
        var type = (t.type || '').toLowerCase();
        var key = labelFor(t);
        // Never buffer a secret field — mirrors extractFields. The thank-you
        // observer ships lastSeenFields wholesale, so a buffered password
        // value would otherwise leak to /api/capture.
        if (isPasswordField(t.name || t.id || key, type)) return;
        // Never buffer a Wix form-app form's values: the thank-you observer would ship them as a capture of a step
        // the visitor never completed. Wix forms are captured only from Wix's own confirmation.
        var cform = formIdOf(t.form);
        if (WIX_FORM_APP_ID.test(cform)) return;
        if (key && t.value != null) { lastSeenFields[key] = String(t.value); lastSeenFormId = cform; }
      });
    }, true);

    // Method 2: fetch / XHR intercept.
    function inspectBody(url, body) {
      // Skip if a native submit fired in the last 2s — the AJAX request is
      // almost certainly that same submission echoing through (Elementor,
      // WPForms, Gravity AJAX, etc.). Native path already captured it with
      // proper labels.
      if (Date.now() - lastNativeSubmitAt < 2000) return;
      // Skip outright if the URL is a known analytics/tracking endpoint.
      // These can post bodies that contain the substring "name"/"email"
      // and accidentally trip looksFormy below.
      if (looksLikeTracker(url)) return;
      safe(function () {
        var fields = null;
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          fields = {};
          body.forEach(function (v, k) { fields[k] = String(v); });
        } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
          fields = {};
          body.forEach(function (v, k) { fields[k] = String(v); });
        } else if (typeof body === 'string') {
          try {
            var parsed = JSON.parse(body);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              var flat = {};
              for (var k in parsed) {
                if (Object.prototype.hasOwnProperty.call(parsed, k) && (typeof parsed[k] === 'string' || typeof parsed[k] === 'number')) {
                  flat[k] = String(parsed[k]);
                }
              }
              fields = flat;
            }
          } catch (e2) {
            // try urlencoded
            try {
              var sp = new URLSearchParams(body);
              var any = false; var f2 = {};
              sp.forEach(function (v, k) { any = true; f2[k] = String(v); });
              if (any) fields = f2;
            } catch (e3) {}
          }
        }
        if (!fields) return;
        // Never transmit secret fields. The network path has no DOM type
        // info, so match on key NAME only. Done BEFORE hasRealContactField so
        // a {email, password} login body keeps email (if that path is
        // intended) but never carries the password value.
        for (var pk in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, pk) && isPasswordField(pk, '')) {
            delete fields[pk];
          }
        }
        // Only treat as a form submission if it carries an actually
        // populated contact-shaped field (email value matching an
        // email regex, or a non-empty phone/name). Stops GA4-style
        // payloads where the body merely references "name" or "email"
        // in metadata without an actual contact value.
        if (!hasRealContactField(fields)) return;
        // Wix's own CreateSubmission POST is read by Method 2b/2c, never captured at request time.
        if (WIX_SUBMIT_URL.test(String(url || ''))) return;
        capture(String(url || location.pathname), fields, { origin: originForm() });
      });
    }

    // QA16 W-FTJS — fetch/XHR AJAX-form-detection wrap MUST fan out to every
    // registered instance, not just the first to install it. `window.fetch`
    // and `XMLHttpRequest.prototype` are process-global — only ONE wrap can
    // ever exist — but each instance still needs ITS OWN `inspectBody`
    // (closure-bound to its own SITE_KEY/API_BASE) to see every body. A bare
    // `!window.fetch.__ft_wrapped` skip-if-any-wrap-exists guard would make
    // the second instance's inspectBody NEVER get called — the second
    // instance would look fully alive (native-submit, pageview, heartbeat all
    // work) while silently losing fetch/XHR AJAX-form-submit detection for
    // its entire page lifetime, with no warning. Fix: keep the wrap itself
    // idempotent (install once), but have it iterate a SHARED array of every
    // registered instance's inspectBody, appended to on every boot.
    window.__ft_body_hooks = window.__ft_body_hooks || [];
    window.__ft_body_hooks.push(inspectBody);

    safe(function () {
      if (window.fetch && !window.fetch.__ft_wrapped) {
        var orig = window.fetch;
        var wrapped = function () {
          var args = arguments;
          safe(function () {
            var url = args[0];
            var init = args[1] || {};
            if (typeof url !== 'string' && url && url.url) url = url.url;
            if (init && init.body) {
              for (var i = 0; i < window.__ft_body_hooks.length; i++) {
                safe(function (hook) { hook(url, init.body); }.bind(null, window.__ft_body_hooks[i]));
              }
            }
          });
          return orig.apply(this, arguments);
        };
        wrapped.__ft_wrapped = true;
        window.fetch = wrapped;
      }
    });

    safe(function () {
      if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__ft_wrapped) {
        var XHR = window.XMLHttpRequest.prototype;
        var origOpen = XHR.open;
        var origSend = XHR.send;
        XHR.open = function (m, u) { this.__ft_url = u; return origOpen.apply(this, arguments); };
        XHR.send = function (body) {
          var self = this;
          safe(function () {
            if (body == null) return;
            for (var i = 0; i < window.__ft_body_hooks.length; i++) {
              safe(function (hook) { hook(self.__ft_url, body); }.bind(null, window.__ft_body_hooks[i]));
            }
          });
          return origSend.apply(this, arguments);
        };
        XHR.__ft_wrapped = true;
      }
    });

    // Method 2b: Wix's own CreateSubmission response, where page fetch/XHR can see it (older form-app). Request
    // time: snapshot the form's values (the form clears on success). Response time: an HTTP 2xx whose submission has
    // a GUID id and status CONFIRMED or PENDING is Wix ACCEPTING (measured on a real site: an ordinary accepted
    // submission answers 200 + PENDING and never later CONFIRMED). 4xx, CAPTCHA errors, PAYMENT_* and anything
    // unparsable are left uncaptured. Independent of the generic wrap above so an earlier script's wrap can't hide it.
    function wixFind(o, key, depth, wantObject) {
      if (!o || typeof o !== 'object' || depth > 4) return undefined;
      var v = o[key];
      if (wantObject ? (v && typeof v === 'object') : typeof v === 'string') return v;
      for (var k in o) {
        if (Object.prototype.hasOwnProperty.call(o, k) && o[k] && typeof o[k] === 'object') {
          var r = wixFind(o[k], key, depth + 1, wantObject);
          if (r) return r;
        }
      }
      return undefined;
    }
    function wixMapFields(map) {
      var out = {};
      if (!map || typeof map !== 'object') return out;
      for (var k in map) {
        if (!Object.prototype.hasOwnProperty.call(map, k) || isPasswordField(k, '')) continue;
        if (typeof map[k] === 'string' || typeof map[k] === 'number') out[k] = String(map[k]);
      }
      return out;
    }
    function hasValue(fields) {
      for (var k in fields) { if (Object.prototype.hasOwnProperty.call(fields, k) && fields[k]) return true; }
      return false;
    }
    function wixSnapshot(body) {
      var parsed = null;
      if (typeof body === 'string') { try { parsed = JSON.parse(body); } catch (e) {} }
      else if (body && typeof body === 'object') parsed = body;
      var formId = wixFind(parsed, 'formId', 0, false);
      var ident = formId ? 'form-' + formId : '';
      var form = ident ? document.getElementById(ident) : null;
      var fields = form ? extractFields(form) : {};
      // A multi-step form keeps only the current step in the DOM: the request's own values fill the gap.
      if (!hasValue(fields)) fields = wixMapFields(wixFind(parsed, 'submissions', 0, true));
      return { ident: ident, fields: fields };
    }
    // Every value Wix accepted: the DOM's labelled fields first, then any value in Wix's own answer that the DOM does
    // not hold (kept under Wix's field key). Reconciled by COUNT, never by value alone: a labelled field absorbs at
    // most ONE of Wix's entries carrying its value; every further entry with that value is a distinct field.
    function wixMergeAnswer(dom, submitted) {
      var out = {};
      var k;
      for (k in dom) { if (Object.prototype.hasOwnProperty.call(dom, k)) out[k] = dom[k]; }
      var have = {};
      for (k in out) { if (Object.prototype.hasOwnProperty.call(out, k) && out[k]) have[out[k]] = (have[out[k]] || 0) + 1; }
      var sub = wixMapFields(submitted);
      var first = function (s) { return String(s).toLowerCase().split(/[^a-z0-9]+/)[0]; };
      var pending = [];
      for (k in sub) { if (sub[k] && !out[k]) pending.push(k); }
      var rest = [];
      pending.forEach(function (p) {
        var named = false;
        if (have[sub[p]] > 0) { for (var l in out) { if (out[l] === sub[p] && first(l) === first(p)) { named = true; break; } } }
        if (named) have[sub[p]]--; else rest.push(p);
      });
      rest.forEach(function (p) { if (have[sub[p]] > 0) have[sub[p]]--; else out[p] = sub[p]; });
      return out;
    }
    function wixSubmitDone(snap, res) {
      var s = res && typeof res === 'object' ? (res.submission || res) : null;
      var id = s && (s.id || s._id);
      if (!s || typeof id !== 'string' || !WIX_GUID.test(id) || (s.status !== 'CONFIRMED' && s.status !== 'PENDING')) return;
      id = id.toLowerCase();
      if (wixConfirmed[id]) return;
      var ident = snap.ident || (typeof s.formId === 'string' ? 'form-' + s.formId : '');
      if (!WIX_FORM_APP_ID.test(ident)) return;
      var fields = wixMergeAnswer(snap.fields, s.submissions);
      if (capture(ident, fields, { evidence: 'wix_submission_confirmed', wixSubmissionId: id, wixSubmissionStatus: s.status })) {
        wixConfirmed[id] = true;
      }
    }
    // Shared by every tracker instance on the page (window.fetch / XHR / MessageChannel are process-global).
    window.__ft_wix_hooks = window.__ft_wix_hooks || [];
    window.__ft_wix_hooks.push(function (body) {
      var snap = wixSnapshot(body);
      return function (res) { wixSubmitDone(snap, res); };
    });
    function wixHooksFor(body) {
      var dones = [];
      for (var i = 0; i < window.__ft_wix_hooks.length; i++) {
        safe(function (h) { dones.push(h(body)); }.bind(null, window.__ft_wix_hooks[i]));
      }
      return dones;
    }
    function wixDeliver(dones, res) {
      for (var i = 0; i < dones.length; i++) safe(function (d) { d(res); }.bind(null, dones[i]));
    }
    safe(function () {
      if (!window.fetch || window.fetch.__ft_wix) return;
      var prevFetch = window.fetch;
      var wixFetch = function (input, init) {
        var p = prevFetch.apply(this, arguments);
        safe(function () {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (!WIX_SUBMIT_URL.test(url)) return;
          if (String((init && init.method) || (input && input.method) || 'GET').toUpperCase() !== 'POST') return;
          var dones = wixHooksFor(init && init.body);
          p.then(function (res) {
            safe(function () {
              if (!res || !res.ok) return;
              res.clone().json().then(function (j) { wixDeliver(dones, j); }, function () {});
            });
          }, function () {});
        });
        return p;
      };
      wixFetch.__ft_wix = true;
      window.fetch = wixFetch;
    });
    safe(function () {
      if (!window.XMLHttpRequest || window.XMLHttpRequest.prototype.__ft_wix) return;
      var xp = window.XMLHttpRequest.prototype;
      var wixOpen = xp.open;
      var wixSend = xp.send;
      xp.open = function (m, u) { this.__ft_wm = m; this.__ft_wu = u; return wixOpen.apply(this, arguments); };
      xp.send = function (b) {
        var x = this;
        safe(function () {
          if (!WIX_SUBMIT_URL.test(String(x.__ft_wu || '')) || String(x.__ft_wm).toUpperCase() !== 'POST') return;
          var dones = wixHooksFor(b);
          x.addEventListener('loadend', function () {
            safe(function () {
              if (x.status < 200 || x.status > 299) return;
              var raw = x.responseType === '' || x.responseType === 'text' ? x.responseText : x.response;
              wixDeliver(dones, typeof raw === 'string' ? JSON.parse(raw) : raw);
            });
          });
        });
        return wixSend.apply(this, arguments);
      };
      xp.__ft_wix = true;
    });

    // Method 2c: Wix's own RESULT channel: how a page script learns the outcome on the current Wix editor.
    // Measured (Wix Harmony): the form-app issues CreateSubmission from inside its clientWorker (invisible to page
    // fetch/XHR), then PUSHES the workflow lifecycle to the form component through a MessageChannel port as
    // `setControllerProps` messages carrying `wfResults[<workflow id>]`: {status:"pending"}, then
    // {status:"failure"|"success", result: <the CreateSubmission response body, or {config, data|response}>}.
    // A normal `defer` Custom Code script runs before Wix creates its channels, so it can listen on them. A "success"
    // for the CreateSubmission URL is judged by the SAME rule as a direct response (wixSubmitDone): nothing invents
    // success. Wix-internal and undocumented: if Wix renames it this observer goes blind and capture FAILS CLOSED.
    var wixPendingSnap = {};
    function wixDomSnapshot() {
      var fid = lastGestureFormId;
      if (!WIX_FORM_APP_ID.test(fid) || Date.now() - lastGestureFormAt > WIX_GESTURE_MS) return { ident: '', fields: {} };
      var f = document.getElementById(fid);
      return { ident: fid, fields: f ? extractFields(f) : {} };
    }
    function wixOnChannelMessage(data) {
      var args = data && data.argumentList;
      if (!args || !args.length) return;
      for (var i = 0; i < args.length; i++) {
        var v = args[i] && args[i].value;
        if (!v || typeof v !== 'object' || !v.wfResults || typeof v.wfResults !== 'object') continue;
        for (var id in v.wfResults) {
          if (!Object.prototype.hasOwnProperty.call(v.wfResults, id)) continue;
          var r = v.wfResults[id];
          if (!r || typeof r !== 'object') continue;
          if (r.status === 'pending') { wixPendingSnap[id] = wixDomSnapshot(); continue; }
          var pending = wixPendingSnap[id];
          delete wixPendingSnap[id];
          if (r.status !== 'success') continue;
          var result = r.result || {};
          var body = result.submission ? result : result.data;
          var cfg = result.config;
          if (!body || !body.submission || typeof body.submission.formId !== 'string') continue;
          if (cfg && cfg.url && !WIX_SUBMIT_URL.test(String(cfg.url))) continue;
          var ident = 'form-' + body.submission.formId;
          // Only a form this page actually renders can be the subject (a stray workflow cannot name one).
          if (!(pending && pending.ident === ident) && !document.getElementById(ident)) continue;
          var snap = pending && pending.ident === ident && hasValue(pending.fields)
            ? pending
            : wixSnapshot(cfg && cfg.data ? cfg.data : { submission: { formId: body.submission.formId } });
          wixSubmitDone(snap, body);
        }
      }
    }
    window.__ft_wix_chan_hooks = window.__ft_wix_chan_hooks || [];
    window.__ft_wix_chan_hooks.push(wixOnChannelMessage);
    safe(function () {
      var OMC = window.MessageChannel;
      if (!OMC || OMC.__ft_wix) return;
      var WMC = function () {
        var c = new OMC();
        safe(function () {
          c.port1.addEventListener('message', function (e) {
            for (var i = 0; i < window.__ft_wix_chan_hooks.length; i++) {
              safe(function (h) { h(e.data); }.bind(null, window.__ft_wix_chan_hooks[i]));
            }
          });
        });
        return c;
      };
      WMC.prototype = OMC.prototype;
      WMC.__ft_wix = true;
      window.MessageChannel = WMC;
    });

    // Method 3: MutationObserver — detect "thank you" content appearing.
    safe(function () {
      if (typeof MutationObserver === 'undefined') return;
      var THANK_RE = /\b(thank you|thanks|received|we['\u2019]?ll be in touch|submission received|got it)\b/i;
      var observer = new MutationObserver(function (records) {
        // Skip if a native submit on this page fired in the last 60s —
        // capture()'s email-keyed dedup would also catch a real dupe,
        // but suppressing here avoids a wasted network round-trip and
        // prevents an unrelated late DOM mutation (chat widget, GDPR
        // banner) from re-firing the buffered email after submit.
        if (Date.now() - lastNativeSubmitAt < 60000) return;
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node && node.nodeType === 1) {
              var text = (node.textContent || '').slice(0, 500);
              if (THANK_RE.test(text)) {
                // Only fire if we have buffered field values.
                var hasAny = false;
                for (var k in lastSeenFields) { if (lastSeenFields[k]) { hasAny = true; break; } }
                if (hasAny) {
                  capture('thankyou:' + location.pathname, lastSeenFields, { origin: originForm() });
                  lastSeenFields = {};
                }
                return;
              }
            }
          }
        }
      });
      observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
    });

    // Method 4: postMessage from embedded iframes (HubSpot, Typeform, Jotform, etc).
    // Drop an attribution breadcrumb the moment a cross-origin form
    // iframe reports activity. The provider's server-to-server webhook
    // arrives seconds later with no FormTrack visitor cookie; the
    // breadcrumb lets webhook-ingest look up the right visitor without
    // any IP or recent-visitor guesswork.
    //
    // Two key shapes:
    //  - submissionId — used by providers that ship the submission id
    //    in their postMessage (Tally, Wufoo, Cognito, Formstack).
    //    Exact match against the webhook's submission id.
    //  - formId — used when the browser never sees a submission id
    //    (Jotform's jsform embed). Webhook ingest picks the most
    //    recent (provider, account, form_id) breadcrumb in a 15-min
    //    window. Drop this one EARLY (e.g. on submission-started)
    //    because Jotform redirects the parent on success, killing the
    //    listener before any post-submit message can be processed.
    function dropAttributionLink(provider, opts) {
      opts = opts || {};
      var submissionId = opts.submissionId ? String(opts.submissionId) : '';
      var formId = opts.formId ? String(opts.formId) : '';
      if (!submissionId && !formId) return;
      // Bail until the cookie bootstrap has issued a visitor_key —
      // the server requires it for the breadcrumb to identify the
      // visitor. The init scan is also retried from inside the
      // initCookie callback so the breadcrumb still fires once the
      // cookie lands.
      if (!VISITOR_KEY) return;
      var url = API_BASE + '/api/forms/attribution-link';
      var payload = {
        site_key: SITE_KEY,
        visitor_key: VISITOR_KEY || '',
        provider: provider,
        // Parent page where the iframe is embedded — used server-side
        // to fill detected_forms.form_page_url for iframe-only providers
        // like Jotform whose webhook payload lacks a reliable webPageURL.
        // Without this the dashboard's "Page they filled" column reads
        // "/ (homepage)" for every Jotform lead because the form row
        // is created from the webhook with form_page_url=null.
        page_url: sanitizeUrl(location.href)
      };
      if (submissionId) payload.submission_id = submissionId;
      if (formId) payload.form_id = formId;
      var body;
      try { body = JSON.stringify(payload); } catch (e) { return; }
      try {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) { /* fall through */ }
      try {
        fetch(url, {
          method: 'POST', body: body, keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', mode: 'cors'
        })['catch'](function () {});
      } catch (e) { /* swallow */ }
    }

    // Detect provider from a host string. Used both for message origins
    // (postMessage path) and iframe.src hosts (DOM-scan path).
    function providerFromHost(host) {
      host = (host || '').toLowerCase();
      if (/jotform\.com$/.test(host)) return 'jotform';
      if (/typeform\.com$/.test(host)) return 'typeform';
      if (/cognitoforms\.com$/.test(host)) return 'cognito';
      if (/wufoo\.com$/.test(host)) return 'wufoo';
      if (/tally\.so$/.test(host)) return 'tally';
      if (/formstack\.com$/.test(host)) return 'formstack';
      return '';
    }
    function providerFromOrigin(origin) {
      try { return providerFromHost(new URL(origin || 'https://x').hostname); }
      catch (e) { return ''; }
    }

    // Pull the provider's form_id out of an iframe src URL.
    function formIdFromSrc(src, provider) {
      if (!src || !provider) return '';
      try {
        if (provider === 'jotform') {
          var m = src.match(/jotform\.com\/(?:jsform\/)?(\d+)/i);
          return m ? m[1] : '';
        }
        if (provider === 'typeform') {
          var t = src.match(/typeform\.com\/to\/([A-Za-z0-9]+)/i);
          return t ? t[1] : '';
        }
        if (provider === 'wufoo') {
          var w = src.match(/wufoo\.com\/(?:embed\/)?([A-Za-z0-9-]+)/i);
          return w ? w[1] : '';
        }
        if (provider === 'cognito') {
          var c = src.match(/cognitoforms\.com\/[^/]+\/([A-Za-z0-9]+)/i);
          return c ? c[1] : '';
        }
        if (provider === 'tally') {
          var ta = src.match(/tally\.so\/(?:r|embed)\/([A-Za-z0-9]+)/i);
          return ta ? ta[1] : '';
        }
        if (provider === 'formstack') {
          var fs = src.match(/formstack\.com\/forms\/(\d+)/i);
          return fs ? fs[1] : '';
        }
      } catch (e) { /* swallow */ }
      return '';
    }

    // Find which iframe on the parent page emitted a postMessage.
    function formIdFromMessageSource(source, providerHint) {
      try {
        var iframes = document.getElementsByTagName('iframe');
        for (var i = 0; i < iframes.length; i++) {
          if (iframes[i].contentWindow === source) {
            return formIdFromSrc(iframes[i].src || '', providerHint);
          }
        }
      } catch (e) { /* swallow */ }
      return '';
    }

    // Throttled form-scoped breadcrumb. We can't rely on Jotform's
    // submit-time postMessages (they're emitted without a targetOrigin
    // in the current bundle, so they never reach the parent — verified
    // against form.jotform.com's jotform.forms.js). Instead we drop a
    // breadcrumb at iframe detection, then refresh it on every message
    // we DO receive from the iframe (setHeight, formSettled, etc all
    // fire correctly with `'*'` targetOrigin). The breadcrumb's
    // created_at is always within seconds of the submit, so the
    // server's `most-recent within 15min` lookup keys on the right
    // visitor — even when the user redirects on submit-success.
    var lastBreadcrumbAt = {};
    function refreshFormBreadcrumb(provider, formId) {
      if (!provider || !formId) return;
      var key = provider + ':' + formId;
      var now = Date.now();
      if (lastBreadcrumbAt[key] && (now - lastBreadcrumbAt[key]) < 3000) return;
      lastBreadcrumbAt[key] = now;
      dropAttributionLink(provider, { formId: formId });
    }

    // Scan the page for known-provider iframes. Called at init and
    // again when MutationObserver sees new iframes. Idempotent due to
    // the throttle in refreshFormBreadcrumb.
    function scanIframesForBreadcrumbs() {
      try {
        var iframes = document.getElementsByTagName('iframe');
        for (var i = 0; i < iframes.length; i++) {
          var src = iframes[i].src || '';
          var host = '';
          try { host = new URL(src, location.href).hostname; } catch (e) { host = ''; }
          var provider = providerFromHost(host);
          if (!provider) continue;
          var formId = formIdFromSrc(src, provider);
          if (formId) refreshFormBreadcrumb(provider, formId);
        }
      } catch (e) { /* swallow */ }
    }

    window.addEventListener('message', function (e) {
      safe(function () {
        var d = e.data;
        var origin = e.origin || '';
        var isSubmit = false; var fields = {}; var ident = '';
        var providerHint = providerFromOrigin(origin);
        var breadcrumbProvider = '';
        var breadcrumbSubmissionId = '';

        // Form-scoped breadcrumb heartbeat. Every message we receive
        // from a known-provider iframe (setHeight, formSettled, layout,
        // etc) refreshes the breadcrumb, throttled per-form. The last
        // refresh before submit lands within seconds of submission,
        // and the webhook ingest looks up the most-recent breadcrumb
        // on `(provider, account, form_id)` to find the right visitor.
        // This avoids Jotform's broken submit-time postMessages
        // entirely (they're emitted without a targetOrigin in
        // jotform.forms.js, so the parent never sees them).
        if (providerHint) {
          var heartbeatFormId = formIdFromMessageSource(e.source, providerHint);
          if (!heartbeatFormId && typeof d === 'string') {
            // Some Jotform messages embed the form id in the string
            // body (e.g. "setHeight:H:formID", "backgroundStyles:...:formID").
            var sm = d.match(/:(\d{10,})\b/);
            if (sm) heartbeatFormId = sm[1];
          }
          if (heartbeatFormId) refreshFormBreadcrumb(providerHint, heartbeatFormId);
        }

        // Jotform string format: "submission-completed:formID:submissionID".
        // Jotform iframes don't ship field values via postMessage — capture the
        // event with whatever same-origin fields we buffered (inline embeds);
        // cross-origin iframes need a Jotform → /api/capture webhook for fields.
        if (typeof d === 'string') {
          if (providerHint === 'jotform' && /submission-completed/i.test(d)) {
            isSubmit = true;
            ident = 'jotform:' + d;
            var parts = d.split(':');
            if (parts.length >= 3) {
              breadcrumbProvider = 'jotform';
              breadcrumbSubmissionId = parts[parts.length - 1];
            }
          }
        } else if (d && typeof d === 'object') {
          // HubSpot — no submission id in the postMessage (HubSpot's
          // webhook keys on `objectId`, which the browser never sees).
          // Still flag as a submit so capture() runs.
          if (d.type === 'hsFormCallback' && d.eventName === 'onFormSubmit') {
            isSubmit = true;
            if (Array.isArray(d.data)) {
              for (var i = 0; i < d.data.length; i++) {
                var f = d.data[i];
                if (f && f.name) fields[f.name] = String(f.value || '');
              }
            } else if (d.data && typeof d.data === 'object') {
              fields = d.data;
            }
          }
          if (d.type === 'form-submit' || d.eventName === 'form_submit') {
            isSubmit = true;
            fields = (d.data && typeof d.data === 'object') ? d.data : (d.formData || {});
          }
          if (d.action === 'submission-completed' || d.type === 'submission-completed') {
            isSubmit = true;
            ident = (providerHint || 'iframe') + ':' + (d.formID || d.formId || 'unknown');
            if (d.submissionID || d.submissionId) {
              breadcrumbProvider = providerHint || 'jotform';
              breadcrumbSubmissionId = String(d.submissionID || d.submissionId);
            }
          }
          // Typeform — `form_submit` from typeform.com origin carries
          // a `response_id` matching the webhook's `event_id` / token.
          if (providerHint === 'typeform' && (d.type === 'form_submit' || d.event === 'form_submit' || d.eventName === 'form_submit')) {
            isSubmit = true;
            var tfId = d.response_id || (d.payload && d.payload.response_id) || d.responseId || '';
            if (tfId) { breadcrumbProvider = 'typeform'; breadcrumbSubmissionId = String(tfId); }
          }
          // Tally — `Tally.formSubmitted` postMessage with `responseId`.
          if (providerHint === 'tally' && (d.event === 'Tally.FormSubmitted' || d.type === 'Tally.FormSubmitted' || d.eventName === 'Tally.FormSubmitted')) {
            isSubmit = true;
            var tlId = (d.payload && (d.payload.responseId || d.payload.submissionId)) || d.responseId || d.submissionId || '';
            if (tlId) { breadcrumbProvider = 'tally'; breadcrumbSubmissionId = String(tlId); }
          }
          // Cognito Forms — embedded form posts an `entry` object with
          // `Id` matching the webhook's `Entry.Id`.
          if (providerHint === 'cognito') {
            var cgId = d.entryId || d.Id || (d.entry && d.entry.Id) || (d.Entry && d.Entry.Id) || '';
            if (cgId && (d.type === 'submitted' || d.eventName === 'submitted' || d.action === 'submitted' || d.event === 'submitted')) {
              isSubmit = true;
              breadcrumbProvider = 'cognito';
              breadcrumbSubmissionId = String(cgId);
            }
          }
          // Wufoo — embedded forms post an `entryId` on submit.
          if (providerHint === 'wufoo') {
            var wfId = d.entryId || d.EntryId || (d.entry && d.entry.id) || '';
            if (wfId && (d.type === 'wufoo:submit' || d.event === 'submit' || d.action === 'submit')) {
              isSubmit = true;
              breadcrumbProvider = 'wufoo';
              breadcrumbSubmissionId = String(wfId);
            }
          }
          // Formstack — embedded forms post a `UniqueId` matching the
          // webhook's submission UniqueId.
          if (providerHint === 'formstack') {
            var fsId = d.UniqueId || d.unique_id || d.submissionId || '';
            if (fsId && (d.type === 'submission' || d.event === 'submission' || d.action === 'submission')) {
              isSubmit = true;
              breadcrumbProvider = 'formstack';
              breadcrumbSubmissionId = String(fsId);
            }
          }
        }

        if (!isSubmit) return;
        if (breadcrumbProvider && breadcrumbSubmissionId) {
          dropAttributionLink(breadcrumbProvider, { submissionId: breadcrumbSubmissionId });
        }
        var fieldsToUse = Object.keys(fields).length ? fields : lastSeenFields;
        capture(ident || ('iframe:' + (origin || 'unknown')), fieldsToUse);
      });
    });

    // Initial sweep + MutationObserver for embedded form iframes.
    // Drops a form-scoped attribution breadcrumb the moment a known
    // provider iframe appears in the DOM, then refreshes it as the
    // iframe sends keepalive postMessages (handled in the listener
    // above). The init-time drop covers single-page loads where the
    // iframe never sends a layout message before submit; the observer
    // covers SPAs and lazy-loaded forms.
    //
    // Race we're avoiding: dropAttributionLink bails when
    // VISITOR_KEY is null (the cookie hasn't been issued yet). If we
    // ran the scan inline at script-eval time, every drop would be a
    // no-op. The init scan + observer therefore ALSO retry once the
    // cookie callback fires AND once /api/pageview has had a chance
    // to commit the visitor row (otherwise the breadcrumb endpoint
    // sees a missing visitor and returns linked:false). That's why
    // the post-cookie kick below schedules a delayed retry.
    function startBreadcrumbScans() {
      safe(scanIframesForBreadcrumbs);
      safe(function () {
        if (typeof MutationObserver === 'undefined') return;
        var iframeObserver = new MutationObserver(function (records) {
          for (var i = 0; i < records.length; i++) {
            var added = records[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
              var node = added[j];
              if (!node || node.nodeType !== 1) continue;
              if (node.tagName === 'IFRAME' || (node.getElementsByTagName && node.getElementsByTagName('iframe').length)) {
                scanIframesForBreadcrumbs();
                return;
              }
            }
          }
        });
        iframeObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
      });
      if (typeof window.addEventListener === 'function') {
        window.addEventListener('load', function () { safe(scanIframesForBreadcrumbs); });
      }
    }
    // Once VISITOR_KEY is populated AND the visitor row has had time
    // to land server-side, retry the iframe sweep. This is the moment
    // the breadcrumb endpoint actually has a row to link against.
    var bcKickFired = false;
    function kickBreadcrumbsAfterPageview() {
      if (bcKickFired) return;
      bcKickFired = true;
      // 800ms gives /api/pageview's upsert a comfortable margin even
      // on slow connections. The throttle inside refreshFormBreadcrumb
      // keeps subsequent setHeight messages from spamming POSTs.
      setTimeout(function () { safe(scanIframesForBreadcrumbs); }, 800);
    }
    // Phase 1: the no-op-but-cheap pre-cookie scan, plus the
    // observer/load handlers that fire later.
    startBreadcrumbScans();

    // Fallback: explicit data-ft-track marker.
    safe(function () {
      var marked = document.querySelectorAll('[data-ft-track]');
      for (var i = 0; i < marked.length; i++) {
        (function (el) {
          el.addEventListener('submit', function () {
            safe(function () {
              capture(el.getAttribute('data-ft-track') || el.id || location.pathname, extractFields(el));
            });
          }, true);
        })(marked[i]);
      }
    });

    // SPA navigation tracking — patch history.pushState/replaceState ONCE
    // (process-global, can't be double-patched). QA16 W-FTJS: found during
    // this build, not in the original recon — the SAME failure mode as the
    // fetch/XHR wrap above. A bare `!history.pushState.__ft_wrapped`
    // skip-if-any-wrap-exists guard would call only the FIRST instance's
    // `pageview` on every route change (it closed over that one instance's
    // function reference), silently losing SPA-navigation tracking for every
    // later instance — and the `popstate` listener lived inside that same
    // guarded block, so a second instance wouldn't even get its own listener
    // added. Fix: the one wrap/listener pair fans out to a SHARED array of
    // every registered instance's own `pageview`, appended to on every boot.
    window.__ft_nav_hooks = window.__ft_nav_hooks || [];
    window.__ft_nav_hooks.push(pageview);
    safe(function () {
      if (history.pushState && !history.pushState.__ft_wrapped) {
        var origPush = history.pushState;
        var origReplace = history.replaceState;
        var fireAll = function () {
          for (var i = 0; i < window.__ft_nav_hooks.length; i++) {
            safe(function (hook) { hook(); }.bind(null, window.__ft_nav_hooks[i]));
          }
        };
        var newPush = function () { var r = origPush.apply(this, arguments); safe(fireAll); return r; };
        var newReplace = function () { var r = origReplace.apply(this, arguments); safe(fireAll); return r; };
        newPush.__ft_wrapped = true;
        newReplace.__ft_wrapped = true;
        history.pushState = newPush;
        history.replaceState = newReplace;
        window.addEventListener('popstate', function () { safe(fireAll); });
      }
    });

    // Honeypots: inject into existing forms now, plus a small
    // MutationObserver that re-runs for forms added later (most SPAs
    // mount their forms client-side after first paint). The same
    // observer feeds the form registry so newly-added forms are
    // pre-registered with the server before anyone submits them.
    function collectFormIdsIn(root) {
      var ids = [];
      try {
        if (!root || typeof root.querySelectorAll !== 'function') return ids;
        var nodes = root.querySelectorAll('form');
        for (var i = 0; i < nodes.length; i++) {
          var id = formIdentifierFor(nodes[i]);
          if (id) ids.push(String(id));
        }
      } catch (e) {}
      return ids;
    }
    safe(function () { injectHoneypotsInto(document); });
    safe(function () {
      if (typeof MutationObserver === 'undefined') return;
      var hpObserver = new MutationObserver(function (records) {
        var added = [];
        for (var i = 0; i < records.length; i++) {
          var addedNodes = records[i].addedNodes;
          for (var j = 0; j < addedNodes.length; j++) {
            var node = addedNodes[j];
            if (!node || node.nodeType !== 1) continue;
            if (node.tagName === 'FORM') {
              injectHoneypot(node);
              var nid = formIdentifierFor(node);
              if (nid) added.push(String(nid));
            } else if (node.querySelectorAll) {
              injectHoneypotsInto(node);
              var ids = collectFormIdsIn(node);
              for (var k = 0; k < ids.length; k++) added.push(ids[k]);
            }
          }
        }
        if (added.length && registry) registry.register(added);
      });
      hpObserver.observe(document.body || document.documentElement, {
        childList: true, subtree: true
      });
    });

    // ── Debug mode ────────────────────────────────────────────────────────
    // Activated by ?leadsource_debug={token} in the page URL. Admin generates
    // a session token in /admin/site-diagnostic, then opens the customer site
    // with that URL param. The script validates the token server-side before
    // enabling any telemetry; invalid tokens are silently ignored.
    //
    // When active, the script publishes structured events to the debug stream
    // at /api/v1/debug/event/{token}. Falls back to an image beacon at
    // /api/v1/debug/beacon/{token} if fetch() is CSP-blocked.
    //
    // NO global window.fetch monkey-patching. Capture flow instrumented
    // directly (see captureWithDebug below).
    // NO heartbeat. NO visual overlay. Auto-expires after 15 minutes.

    var DEBUG_TOKEN = null;        // 64-char hex token if debug mode active
    var DEBUG_SESSION_ID = null;   // UUID from validate-token response
    var DEBUG_EXPIRES_AT = 0;      // ms timestamp — 0 = inactive

    // sessionStorage keys for same-tab multi-page debug session persistence.
    // N-instance hardening: namespaced by INSTANCE_KEY, same rationale as
    // SESSION_KEY above — no external code reads these by their old fixed
    // names (checked), so each instance gets its own debug-session
    // persistence instead of N instances silently overwriting one shared
    // slot. (window.__leadsource_debug_session_id and
    // window.__leadsource_trigger_synthetic_capture below stay UNNAMESPACED,
    // deliberately — see their own comment for why.)
    var SS_DEBUG_TOKEN = '__leadsource_debug_token_' + INSTANCE_KEY;
    var SS_DEBUG_SESSION_ID = '__leadsource_debug_session_id_' + INSTANCE_KEY;
    var SS_DEBUG_EXPIRES_AT = '__leadsource_debug_expires_at_' + INSTANCE_KEY;

    function clearDebugSessionStorage() {
      safe(function () {
        sessionStorage.removeItem(SS_DEBUG_TOKEN);
        sessionStorage.removeItem(SS_DEBUG_SESSION_ID);
        sessionStorage.removeItem(SS_DEBUG_EXPIRES_AT);
      });
    }

    function saveDebugSessionStorage(token, sessionId, expiresAt) {
      // Wrapped in safe() — throws silently if sessionStorage is full or blocked
      // (privacy mode, iOS restrictions). Debug from URL still works; only
      // multi-page persistence is lost.
      safe(function () {
        sessionStorage.setItem(SS_DEBUG_TOKEN, token);
        sessionStorage.setItem(SS_DEBUG_SESSION_ID, sessionId);
        sessionStorage.setItem(SS_DEBUG_EXPIRES_AT, expiresAt);
      });
    }

    function publishDebugEvent(eventType, payload) {
      if (!DEBUG_TOKEN || Date.now() > DEBUG_EXPIRES_AT) return;
      var url = API_BASE + '/api/v1/debug/event/' + DEBUG_TOKEN;
      var body;
      try { body = JSON.stringify({ event_type: eventType, payload: payload || {} }); } catch (e) { return; }
      var sent = false;
      try {
        if (typeof window.fetch === 'function') {
          window.fetch(url, {
            method: 'POST',
            body: body,
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors'
          })['catch'](function (err) {
            // CSP-blocked fetch — fall back to image beacon
            if (!sent && err && /blocked|CSP|policy/i.test(String(err.message || ''))) {
              sent = true;
              safe(function () {
                var img = new Image();
                img.src = API_BASE + '/api/v1/debug/beacon/' + DEBUG_TOKEN + '?event=csp_blocked';
              });
            }
          });
          sent = true;
        }
      } catch (e) {
        // fetch itself threw (strict CSP blocking the call) — fall back to beacon
        if (!sent) {
          safe(function () {
            var img = new Image();
            img.src = API_BASE + '/api/v1/debug/beacon/' + DEBUG_TOKEN + '?event=csp_blocked';
          });
        }
      }
    }

    function extractFormMeta(form) {
      try {
        var fields = [];
        var inputs = form.querySelectorAll('input, select, textarea');
        for (var i = 0; i < Math.min(inputs.length, 20); i++) {
          var inp = inputs[i];
          // Skip the controls that are not data-bearing, so the reported count
          // is the count of fields a human would say the form HAS.
          var mt = (inp.type || '').toLowerCase();
          if (mt === 'submit' || mt === 'button' || mt === 'reset' || mt === 'image' || mt === 'file' || mt === 'hidden') continue;
          // Identify by name, then id, then aria-label, then the placeholder=
          // attribute.
          //
          // This used to be `if (!inp.name) continue;`, which silently dropped
          // every control that has no name attribute. On a real Wix Forms page
          // (measured on bettergardens.ae/contact, form-app 1.3080.0) the
          // contact form has SIX data-bearing controls and exactly ONE of them
          // carries a name — so this reported `field_count: 1` for a six-field
          // form, on every detection. That is the "8 detections, all 1 field"
          // symptom: not eight odd forms, one bad predicate.
          var ident =
            inp.name ||
            inp.id ||
            (inp.getAttribute && inp.getAttribute('aria-label')) ||
            (inp.getAttribute && inp.getAttribute('placeholder')) ||
            '';
          fields.push({ name: String(ident), type: inp.type || inp.tagName.toLowerCase(), required: !!inp.required });
        }
        return {
          id: form.id || '',
          name: form.name || '',
          action: form.action || '',
          method: (form.method || 'get').toUpperCase(),
          field_count: fields.length,
          fields: fields
        };
      } catch (e) { return {}; }
    }

    // Wrapper that publishes capture_attempt + capture_result around the
    // existing capture() logic. Replaces capture() only when debug mode is active.
    // ── TWO DEFECTS FIXED HERE, BOTH INSTRUMENTATION ────────────────────────
    //
    // 1. `opts` was DROPPED. The signature took two parameters and forwarded
    //    two, while Method 1 and Method 1b both call
    //    `capture(id, fields, detectPasswordSignals(form))`. So whenever a
    //    debug session was active, the value-free risky-form signals silently
    //    vanished and login/signup classification degraded — on exactly the
    //    sessions an operator is watching.
    //
    // 2. `dispatched` was the literal `true`, whatever capture() did. A call
    //    suppressed by client dedup, by the excluded-form registry or by the
    //    empty-fields gate still reported "dispatched: yes". That is not a
    //    cosmetic bug: it made the 2026-09-21 duplicate-capture report
    //    impossible to settle from the stream, because a working dedup and a
    //    broken one print the same line. capture() now returns whether it
    //    actually sent, and this reports that.
    var _originalCapture = capture;
    function captureWithDebug(formIdentifier, fields, opts) {
      if (!DEBUG_TOKEN || Date.now() > DEBUG_EXPIRES_AT) {
        return _originalCapture(formIdentifier, fields, opts);
      }
      publishDebugEvent('capture_attempt', {
        form_identifier: String(formIdentifier || ''),
        field_count: fields ? Object.keys(fields).length : 0
      });
      var dispatched = _originalCapture(formIdentifier, fields, opts) === true;
      // capture_result fires synchronously after capture() returns. send() uses
      // sendBeacon (fire-and-forget), so `dispatched: true` means "handed to
      // the browser", never "the server accepted it" — the server publishes its
      // own capture_result for that, and the two are distinguished in the panel
      // by `server_emitted`.
      // Include the submitted field values so the operator's diagnostic
      // panel can show what the test form carried. This is acceptable
      // here because (a) it's the operator's own test data, (b) the
      // debug session expires after 15 min, and (c) the persistent
      // customer-data surface (admin Site Inspector) is metadata-only.
      publishDebugEvent('capture_result', {
        form_identifier: String(formIdentifier || ''),
        dispatched: dispatched,
        fields: fields || {}
      });
      return dispatched;
    }

    // Exposed on window when debug mode is active. Called by the admin UI's
    // "Trigger synthetic capture" button to send a test submission with the
    // X-Lead-Source-Synthetic header, routing it to synthetic_captures (never leads).
    function triggerSyntheticCapture() {
      if (!DEBUG_TOKEN || Date.now() > DEBUG_EXPIRES_AT) return;
      var payload = {
        submission_id: uuidv4(),
        site_key: SITE_KEY,
        visitor_key: VISITOR_KEY || uuidv4(),
        page_url: sanitizeUrl(location.href),
        page_path: location.pathname,
        referrer: '',
        form_identifier: 'synthetic_diagnostic_test',
        fields: { email: 'synthetic@leadsource.test', name: 'Diagnostic Test' },
        session_id: SESSION_ID,
        script_version: SCRIPT_VERSION,
        timestamp: new Date().toISOString(),
        utm_source: '', utm_medium: '', utm_campaign: '',
        utm_term: '', utm_content: '', gclid: '', fbclid: '', msclkid: ''
      };
      publishDebugEvent('capture_attempt', { form_identifier: payload.form_identifier, field_count: 2, synthetic: true });
      try {
        fetch(API_BASE + '/api/capture', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Lead-Source-Synthetic': 'true' },
          body: JSON.stringify(payload),
          mode: 'cors'
        }).then(function (r) {
          return r.json();
        }).then(function (d) {
          publishDebugEvent('capture_result', { synthetic: true, ok: !!(d && d.status === 'synthetic_ok'), status: d && d.status });
        })['catch'](function (e) {
          publishDebugEvent('error', { message: 'synthetic_capture_failed: ' + (e && e.message ? e.message : 'network'), synthetic: true });
        });
      } catch (e) {
        publishDebugEvent('error', { message: 'synthetic_capture_error', synthetic: true });
      }
    }

    function startDebugMode(token, sessionId, expiresAt) {
      DEBUG_TOKEN = token;
      DEBUG_SESSION_ID = sessionId;
      DEBUG_EXPIRES_AT = new Date(expiresAt).getTime();
      // N-instance audit: deliberately UNNAMESPACED, unlike every other
      // shared global in this file. app/admin/site-diagnostic's
      // DiagnosticPanel.tsx calls window.__leadsource_trigger_synthetic_capture()
      // by this exact fixed name (tests/diagnostic-globals-parity.test.ts
      // pins it) — renaming or keying it per-instance would break that
      // existing, tested, external contract. This is also the correct
      // DESIGN for what the admin tool actually does: an admin debugging one
      // specific site's tracker wants THAT tracker's synthetic-capture
      // trigger reachable at one well-known name, not fanned out across
      // every other tracker that happens to share the page — debug mode is
      // a single-focus diagnostic affordance, not a customer-facing
      // multi-instance capture guarantee (the fetch/XHR/nav fan-out below
      // IS that guarantee, and does fan out to all instances). Last-instance
      // -to-activate-debug wins here, unchanged from before this hardening.
      window.__leadsource_debug_session_id = sessionId;
      window.__leadsource_trigger_synthetic_capture = triggerSyntheticCapture;

      // Redirect capture() to the instrumented wrapper
      capture = captureWithDebug;

      publishDebugEvent('script_loaded', {
        script_version: SCRIPT_VERSION,
        site_key: SITE_KEY,
        page_url: sanitizeUrl(location.href),
        user_agent: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : ''
      });

      // Scan and report detected forms immediately
      safe(function () {
        var forms = document.querySelectorAll('form');
        for (var i = 0; i < forms.length; i++) {
          publishDebugEvent('form_detected', extractFormMeta(forms[i]));
        }
      });

      // Auto-expire: clear debug state when session expires
      var msUntilExpiry = DEBUG_EXPIRES_AT - Date.now();
      if (msUntilExpiry > 0) {
        setTimeout(function () {
          publishDebugEvent('session_end', { reason: 'expired' });
          DEBUG_TOKEN = null;
          DEBUG_SESSION_ID = null;
          DEBUG_EXPIRES_AT = 0;
          window.__leadsource_debug_session_id = null;
          capture = _originalCapture;
          // Clear sessionStorage so the next page load doesn't re-attempt
          // a session we already know has expired locally.
          clearDebugSessionStorage();
        }, msUntilExpiry);
      }

      // Capture JS errors that occur during the session
      if (typeof window.addEventListener === 'function') {
        window.addEventListener('error', function (e) {
          if (!DEBUG_TOKEN) return;
          publishDebugEvent('error', {
            message: e.message || '',
            stack: sanitizeStack(e.error && e.error.stack ? e.error.stack : ''),
            source: e.filename ? e.filename.split('?')[0] : ''
          });
        });
      }
    }

    // Detect and activate debug mode.
    //
    // Order of precedence:
    //   1. URL param (?leadsource_debug=<token>) — always wins, overwrites sessionStorage.
    //   2. sessionStorage — enables multi-page debugging within the same tab without
    //      the URL param surviving every navigation.
    //
    // In both paths the token is validated server-side on every page load so the
    // admin can end the session at any time and the next page load will deactivate.
    safe(function () {
      var params = new URLSearchParams(location.search);
      var urlToken = params.get('leadsource_debug');

      if (urlToken && urlToken.length === 64) {
        // URL param present — validate server-side, then persist to sessionStorage
        // and activate. Overwrites any existing sessionStorage session so opening
        // a new debug URL always starts fresh.
        fetch(API_BASE + '/api/v1/debug/validate-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken, site_id: SITE_KEY }),
          mode: 'cors'
        }).then(function (r) {
          return r.ok ? r.json() : null;
        }).then(function (d) {
          if (!d || !d.valid || !d.session_id || !d.expires_at) {
            // Invalid token — clear any stale sessionStorage session.
            clearDebugSessionStorage();
            return;
          }
          saveDebugSessionStorage(urlToken, d.session_id, d.expires_at);
          startDebugMode(urlToken, d.session_id, d.expires_at);
        })['catch'](function () { /* silently ignore — network errors don't break production flow */ });
        return;
      }

      // No URL param — check sessionStorage for a persisted session from an
      // earlier page in this tab.
      var cachedToken = safe(function () { return sessionStorage.getItem(SS_DEBUG_TOKEN); });
      if (!cachedToken || cachedToken.length !== 64) return;

      // Local expiry check: avoid the server round-trip if we already know
      // the session has expired (saves a fetch when the session timed out
      // naturally while the tab was open).
      var cachedExpiresAt = safe(function () { return sessionStorage.getItem(SS_DEBUG_EXPIRES_AT); });
      if (cachedExpiresAt && Date.now() >= new Date(cachedExpiresAt).getTime()) {
        clearDebugSessionStorage();
        return;
      }

      // Server-side validation — the admin may have ended the session before
      // the local expires_at was reached.
      fetch(API_BASE + '/api/v1/debug/validate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: cachedToken, site_id: SITE_KEY }),
        mode: 'cors'
      }).then(function (r) {
        return r.ok ? r.json() : null;
      }).then(function (d) {
        if (!d || !d.valid || !d.session_id || !d.expires_at) {
          // Session ended server-side — clear so subsequent page loads don't
          // keep re-validating a dead token.
          clearDebugSessionStorage();
          return;
        }
        // Refresh sessionStorage with the server's latest expires_at (in case
        // the session was extended) and activate debug mode.
        saveDebugSessionStorage(cachedToken, d.session_id, d.expires_at);
        startDebugMode(cachedToken, d.session_id, d.expires_at);
      })['catch'](function () { /* network error — fail-open, no debug mode activated */ });
    });

    // Boot: get visitor cookie, then send first pageview, then register
    // every form already on the page so the customer's /install registry
    // reflects the site before anyone submits.
    initCookie(function () {
      // Wire dwell listeners now that FLAGS is populated (8a). No-op when the
      // dwell flag is off; isolated from capture (INV-2). pageview() below mints
      // the first client_pv_id and starts the accumulator if the flag is on.
      safe(setupDwell);
      safe(pageview);
      safe(function () {
        registry = createRegistry({
          siteKey: SITE_KEY,
          apiBase: API_BASE,
          pageUrl: sanitizeUrl(location.href)
        });
        registry.register(collectFormIdsIn(document));
      });
      // Now that VISITOR_KEY is set and pageview is in flight, kick
      // the breadcrumb scan after a short delay so the visitor row
      // has time to commit before /api/forms/attribution-link is
      // called. Without this, the init-time scan saw no VISITOR_KEY
      // and was a no-op, the heartbeat path didn't fire fast enough
      // for some embeds, and webhooks attached to stale breadcrumbs
      // from earlier sessions.
      safe(kickBreadcrumbsAfterPageview);
    });
  } catch (e) {
    try { console.error('[ft] fatal', e); } catch (_) {}
  }
})();
